import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { registerClient, getClientsByRut, lookupDteTaxpayer } from '../api';
import { CLIENT_TYPE_OPTIONS } from '../utils/clientType';
import useCurrentUser from '../hooks/useCurrentUser';
import { hasPermission } from '../utils/permissions';

const ModalClientRegistration = ({
    isOpen,
    onClose,
    name,
    setName,
    searchInputRef,
    setClientId,
    rut,
    setRut,
    email,
    setEmail,
    address,
    setAddress,
    city,
    setCity,
    comuna = '',
    setComuna = () => {},
    giro = '',
    setGiro = () => {},
    phone,
    setPhone,
    savePurchase,
    setModalVarietyPrice,
    allowContinueWithoutClient = false,
    clientType,
    setClientType,
    onClientSelected,
    requireClient = false,
    clientTypeOptions = CLIENT_TYPE_OPTIONS,
    canCreateClient = true,
    canReadClient = true,
    detailActionLabel = 'Venta Detalle',
    cancelLabel = 'Deshacer Venta',
}) => {
    const [showFields, setShowFields] = useState(false);
    const navigate = useNavigate();
    const branchId = useParams().branchId || localStorage.getItem('selectedBranchId');
    const [typeError, setTypeError] = useState('');
    const [siiLookupLoading, setSiiLookupLoading] = useState(false);
    const [siiLookupError, setSiiLookupError] = useState('');
    const { user } = useCurrentUser();
    const canLookupTaxpayer = hasPermission(user, 'client.sii_lookup') || hasPermission(user, 'dte.settings');
    const [selectingTypeOnly, setSelectingTypeOnly] = useState(false);
    const searchDebounceRef = useRef(null);
    const lookupRequestIdRef = useRef(0);
    const lastLookupRutRef = useRef('');
    const resolvedClientTypeOptions =
        Array.isArray(clientTypeOptions) && clientTypeOptions.length
            ? clientTypeOptions
            : CLIENT_TYPE_OPTIONS;

    const formatRut = (e) => {
        setRut(e.target.value
            .replace(/[^0-9kK]/g, '')
            .replace(/^(\d{1,2})(\d{3})(\d{3})([0-9kK])$/, '$1.$2.$3-$4')
            .toUpperCase())
    }

    const normalizeRutForLookup = (value) =>
        String(value || '').replace(/[^0-9kK]/g, '').toUpperCase();

    const clearSelectedClient = useCallback(async ({ persist = false, source = 'auto-clear' } = {}) => {
        const hadClientData = Boolean(name || email || address || city || comuna || giro || phone);
        setName('');
        setEmail('');
        setAddress('');
        setCity('');
        setComuna('');
        setGiro('');
        setPhone('');
        setClientId(null);
        onClientSelected && onClientSelected(null, { source });
        if (persist && hadClientData && typeof savePurchase === 'function') {
            await savePurchase(null);
        }
    }, [
        address,
        city,
        comuna,
        email,
        giro,
        name,
        onClientSelected,
        phone,
        savePurchase,
        setAddress,
        setCity,
        setComuna,
        setClientId,
        setEmail,
        setGiro,
        setName,
        setPhone,
    ]);

    const applyClientSelection = useCallback(async (client, options = {}) => {
        const { source = 'manual' } = options;
        if (!client) return false;
        setName(client.name || '');
        setEmail(client.email || '');
        setAddress(client.address || '');
        setCity(client.city || '');
        setComuna(client.comuna || '');
        setGiro(client.giro || '');
        setPhone(client.phone || '');
        setClientId(client.id);
        onClientSelected && onClientSelected(client, { source });
        setShowFields(false);
        if (typeof savePurchase === 'function') {
            await savePurchase(client.id);
        }
        return true;
    }, [
        onClientSelected,
        savePurchase,
        setAddress,
        setCity,
        setComuna,
        setClientId,
        setEmail,
        setGiro,
        setName,
        setPhone,
    ]);

    const lookupClientByRut = useCallback(async (rutValue, options = {}) => {
        if (!canReadClient) return false;
        const {
            showRegisterFieldsOnMiss = true,
            closeOnFound = false,
            clearSelectionOnMiss = false,
            lookupSource = 'manual-search',
        } = options;
        const normalizedRut = normalizeRutForLookup(rutValue);
        if (normalizedRut.length < 8) return false;
        const requestId = ++lookupRequestIdRef.current;
        try {
            const clients = await getClientsByRut(rutValue);
            if (requestId !== lookupRequestIdRef.current) return false;
            const client = Array.isArray(clients) && clients.length ? clients[clients.length - 1] : null;
            if (client) {
                await applyClientSelection(client, { source: lookupSource });
                if (closeOnFound) {
                    onClose(false);
                }
                return true;
            }
            if (clearSelectionOnMiss) {
                await clearSelectedClient({ persist: true, source: 'auto-clear' });
            }
            if (showRegisterFieldsOnMiss) {
                setShowFields(true);
            }
            return false;
        } catch (error) {
            if (requestId === lookupRequestIdRef.current) {
                console.error(error);
            }
            return false;
        }
    }, [applyClientSelection, canReadClient, clearSelectedClient, onClose]);

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        if (!canCreateClient) return;

        try {
            const response = await registerClient({ name, rut, email, address, city, comuna, giro, phone });
            const client = response.client;

            if (client) {
                setName(client.name);
                setEmail(client.email);
                setAddress(client.address);
                setCity(client.city);
                setComuna(client.comuna || '');
                setGiro(client.giro || '');
                setPhone(client.phone);
                setClientId(client.id);
                onClientSelected && onClientSelected(client, { source: 'register' });
                savePurchase(client.id)
                onClose(false);
            }
        } catch (error) {
            console.error(error);
        }
    };

    const handleSiiLookup = async () => {
        setSiiLookupError('');
        if (!canLookupTaxpayer) return;
        setSiiLookupLoading(true);
        try {
            const response = await lookupDteTaxpayer({ rut });
            const taxpayer = response?.taxpayer || response || {};
            const actividades = Array.isArray(taxpayer.actividadesEconomicas)
                ? taxpayer.actividadesEconomicas
                : [];
            const domicilios = Array.isArray(taxpayer.domicilios) ? taxpayer.domicilios : [];
            const firstActivity = actividades[0] || {};
            const firstAddress = domicilios[0] || {};
            setName(taxpayer.razonSocial || taxpayer.nombre || name || '');
            setGiro(
                firstActivity.giro
                || firstActivity.actividad
                || firstActivity.descripcion
                || giro
                || ''
            );
            setAddress(firstAddress.direccion || firstAddress.calle || address || '');
            setComuna(firstAddress.comuna || comuna || '');
            setCity(firstAddress.ciudad || firstAddress.region || city || '');
            setShowFields(true);
        } catch (error) {
            setSiiLookupError(error.response?.data?.message || 'No se pudo consultar el RUT en SII.');
        } finally {
            setSiiLookupLoading(false);
        }
    };

    const handleDetailSell = async () => {
        if (allowContinueWithoutClient) {
            if (!selectingTypeOnly) {
                setSelectingTypeOnly(true);
                return;
            }
            if (!setClientType || !clientType) {
                setTypeError('Selecciona un tipo de cliente para continuar sin cliente.');
                return;
            }
            setTypeError('');
            setRut("");
            onClose(false);
            return;
        }
        setRut("");
        onClose(false);
    }

    const handleSearchByRut = async () => {
        await lookupClientByRut(rut, {
            showRegisterFieldsOnMiss: true,
            closeOnFound: true,
            clearSelectionOnMiss: false,
            lookupSource: 'manual-search',
        });
    };

    useEffect(() => {
        if (isOpen) return;
        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
            searchDebounceRef.current = null;
        }
        lastLookupRutRef.current = '';
        lookupRequestIdRef.current += 1;
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen || !canReadClient) return undefined;
        const normalizedRut = normalizeRutForLookup(rut);
        if (normalizedRut.length < 8) {
            lastLookupRutRef.current = '';
            return undefined;
        }
        if (lastLookupRutRef.current === normalizedRut) {
            return undefined;
        }
        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
        }
        searchDebounceRef.current = setTimeout(() => {
            lastLookupRutRef.current = normalizedRut;
            lookupClientByRut(rut, {
                showRegisterFieldsOnMiss: true,
                closeOnFound: false,
                clearSelectionOnMiss: true,
                lookupSource: 'auto',
            });
        }, 350);
        return () => {
            if (searchDebounceRef.current) {
                clearTimeout(searchDebounceRef.current);
            }
        };
    }, [isOpen, rut, canReadClient, lookupClientByRut]);

    if (!isOpen) {
        return null;
    }

    // if (clientExists) {
    //     return (
    //         <div className={`fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 ${isOpen ? '' : 'hidden'
    //             }`}>
    //                 <div className="bg-white w-full md:w-1/2 rounded-lg">
    //                 <div
    //                     className="p-4 bg-gray-200 rounded-t-lg"
    //                 >
    //                     <h2>Cliente Encontrado</h2>
    //                 </div>
    //                 <div className='p-6'>
    //                     <p>{name}</p>
    //                     <p>{rut}</p>
    //                     <p>{email}</p>
    //                     <p>{address}</p>
    //                     <p>{city}</p>
    //                     <p>{phone}</p>
    //                 </div>
    //             </div>
    //         </div>
    //     )
    // }

    return (
        <div
            className={`fixed inset-0 z-[1000] flex items-start justify-center bg-black/50 px-4 py-6 overflow-y-auto ${isOpen ? '' : 'hidden'}`}
            onClick={(event) => event.stopPropagation()}
        >
            <div
                className="bg-white w-full max-w-3xl rounded-2xl shadow-2xl border border-gray-100 overflow-hidden max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-start justify-between px-6 py-4 border-b border-gray-100 bg-gray-50">
                    <div>
                        <p className="text-xs uppercase tracking-wide text-gray-500">Cliente</p>
                        <h2 className="text-lg font-semibold text-gray-900">Registrar cliente</h2>
                        <p className="text-xs text-gray-500 mt-1">
                            Busca por RUT o ingresa la información del cliente.
                        </p>
                    </div>
                    <button
                        onClick={() => onClose(false)}
                        className="text-gray-600 hover:text-gray-900 bg-white border border-gray-200 rounded-full w-9 h-9 flex items-center justify-center"
                    >
                        ✕
                    </button>
                </div>

                <div className="p-6 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="md:col-span-2">
                            <label className="text-xs font-semibold text-gray-600 uppercase">RUT</label>
                            <input
                                type="text"
                                placeholder="12.345.678-9"
                                value={rut}
                                onChange={formatRut}
                                autoComplete="off"
                                className="mt-2 p-3 border border-gray-300 rounded-lg w-full"
                            />
                            {canLookupTaxpayer && (
                            <div className="mt-2 flex flex-wrap items-center gap-2">
                                <button
                                    type="button"
                                    className="px-3 py-2 text-sm text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-50 disabled:text-gray-400 disabled:border-gray-200"
                                    onClick={handleSiiLookup}
                                    disabled={rut.length < 8 || siiLookupLoading}
                                >
                                    {siiLookupLoading ? 'Consultando...' : 'Actualizar desde SII'}
                                </button>
                                {siiLookupError && <span className="text-xs text-red-600">{siiLookupError}</span>}
                            </div>
                            )}
                        </div>

                        {allowContinueWithoutClient && selectingTypeOnly && (
                            <div className="md:col-span-2 flex flex-col gap-1">
                                <label className="text-xs font-semibold text-gray-600 uppercase">Tipo de cliente</label>
                                <select
                                    className="p-3 border border-gray-300 rounded-lg"
                                    value={clientType || ''}
                                    onChange={(e) => {
                                        setTypeError('');
                                        setClientType && setClientType(e.target.value);
                                    }}
                                >
                                    <option value="">Seleccionar</option>
                                    {resolvedClientTypeOptions.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                                {typeError && <p className="text-xs text-red-600">{typeError}</p>}
                            </div>
                        )}
                    </div>

                    {showFields && (
                        <div className="space-y-4">
                            {!canCreateClient && (
                                <p className="text-sm text-red-600">
                                    No tienes permisos para registrar clientes.
                                </p>
                            )}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="md:col-span-2">
                                    <label className="text-xs font-semibold text-gray-600 uppercase">Nombre</label>
                                    <input
                                        type="text"
                                        placeholder="Nombre"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        disabled={!rut}
                                        autoComplete="off"
                                        className="mt-2 p-3 border border-gray-300 rounded-lg w-full"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-semibold text-gray-600 uppercase">Email</label>
                                    <input
                                        type="email"
                                        placeholder="Email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        autoComplete="off"
                                        disabled={!rut}
                                        className="mt-2 p-3 border border-gray-300 rounded-lg w-full"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-semibold text-gray-600 uppercase">Teléfono</label>
                                    <input
                                        type="text"
                                        placeholder="Teléfono"
                                        value={phone}
                                        onChange={(e) => setPhone(e.target.value)}
                                        disabled={!rut}
                                        className="mt-2 p-3 border border-gray-300 rounded-lg w-full"
                                    />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="text-xs font-semibold text-gray-600 uppercase">Giro</label>
                                    <input
                                        type="text"
                                        placeholder="Giro"
                                        value={giro}
                                        onChange={(e) => setGiro(e.target.value)}
                                        disabled={!rut}
                                        maxLength={40}
                                        className="mt-2 p-3 border border-gray-300 rounded-lg w-full"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-semibold text-gray-600 uppercase">Dirección</label>
                                    <input
                                        type="text"
                                        placeholder="Dirección"
                                        value={address}
                                        onChange={(e) => setAddress(e.target.value)}
                                        disabled={!rut}
                                        maxLength={70}
                                        className="mt-2 p-3 border border-gray-300 rounded-lg w-full"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-semibold text-gray-600 uppercase">Comuna</label>
                                    <input
                                        type="text"
                                        placeholder="Comuna"
                                        value={comuna}
                                        onChange={(e) => setComuna(e.target.value)}
                                        disabled={!rut}
                                        maxLength={20}
                                        className="mt-2 p-3 border border-gray-300 rounded-lg w-full"
                                    />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="text-xs font-semibold text-gray-600 uppercase">Ciudad</label>
                                    <input
                                        type="text"
                                        placeholder="Ciudad"
                                        value={city}
                                        onChange={(e) => setCity(e.target.value)}
                                        disabled={!rut}
                                        className="mt-2 p-3 border border-gray-300 rounded-lg w-full"
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 border-t border-gray-100 pt-4">
                        {!requireClient && (
                            <button
                                className="px-4 py-2 text-white rounded-lg bg-blue-600 hover:bg-blue-700"
                                onClick={handleDetailSell}
                            >
                                {allowContinueWithoutClient
                                    ? (selectingTypeOnly ? 'Confirmar sin cliente' : 'Continuar sin cliente')
                                    : detailActionLabel}
                            </button>
                        )}
                        <div className="flex flex-wrap gap-2">
                            <button
                                className="px-4 py-2 text-gray-700 rounded-lg border border-gray-200 hover:text-gray-900"
                                onClick={() => { setModalVarietyPrice(null); onClose(false); }}
                            >
                                {cancelLabel}
                            </button>
                            {showFields ? (
                                canCreateClient && (
                                    <button
                                        className="px-4 py-2 text-white rounded-lg bg-blue-600 hover:bg-blue-700"
                                        onClick={handleFormSubmit}
                                    >
                                        Registrar
                                    </button>
                                )
                            ) : (
                                <button
                                    className={`px-4 py-2 text-white rounded-lg ${
                                        rut.length < 8 || !canReadClient ? "bg-gray-500" : "bg-blue-600 hover:bg-blue-700"
                                    }`}
                                    onClick={handleSearchByRut}
                                    disabled={rut.length < 8 || !canReadClient}
                                >
                                    Buscar
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ModalClientRegistration;
